import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Button, Space } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { REPORT_STATUS, REPORT_STATUS_VALUES } from 'constants/reports';
import { useAppDispatch } from 'store/hooks';
import { setViewerPdf } from 'store/file/slice';
import { setTour } from 'store/tour/slice';
import { ColumnProps } from 'antd/lib/table';
import { ReportRow, fetchReports } from 'services/api/reports';

import { showRestError } from 'utils/showErrorMessage';
import { ViewReport } from 'components/shared';

const PAGE_LIMIT = 10;

const PersonalReports: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') || 1);

  const [reports, setReports] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const startDate = searchParams.get('startDate') || undefined;
  const endDate = searchParams.get('endDate') || undefined;
  const name = searchParams.get('name') || undefined;

  useEffect(() => {
    setLoading(true);
    // v2 `type=draft` = personal scope (reports without an appointment)
    fetchReports({
      startDate,
      endDate,
      name,
      type: REPORT_STATUS.draft,
      page,
      limit: PAGE_LIMIT
    })
      .then(res => {
        setReports(res.data);
        setTotal(res.total);
        dispatch(setTour({ key: 'personalReport', value: true }));
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [dispatch, startDate, endDate, name, page]);

  const onDownloadReport = useCallback(
    (id: string) => {
      dispatch(setViewerPdf({ id, url: `appointments-reports/pdf/${id}` }));
    },
    [dispatch]
  );

  const columns: ColumnProps<ReportRow>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('reports.creation_date'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => dayjs(value).format(FULL_DATE_FORMAT)
      },
      {
        title: t('reports.patient_name'),
        dataIndex: 'firstName',
        key: 'firstName',
        render: (_, record) => `${record.firstName} ${record.lastName}`
      },
      {
        title: t('reports.report_type'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => t(`reports.${REPORT_STATUS_VALUES[status]}`)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (id, record) =>
          record.status === REPORT_STATUS.draft ? (
            <div data-element="edit-report-button">
              <Link to={`/report/details/${id}`}>
                <Button icon={<EditOutlined />} style={{ minWidth: 135 }}>
                  {t('reports.edit_report')}
                </Button>
              </Link>
            </div>
          ) : (
            <Space>
              <Link
                to={`/pdf-viewer/${id}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => onDownloadReport(id)}
              >
                <Button icon={<DownloadOutlined />} type="primary" ghost>
                  {t('reports.download')}
                </Button>
              </Link>
              <ViewReport id={id} />
            </Space>
          )
      }
    ],
    [onDownloadReport, t]
  );

  return (
    <Table
      dataSource={reports}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination={{
        total: total || 0,
        current: page,
        onChange: p => {
          searchParams.set('page', `${p}`);
          setSearchParams(searchParams);
        }
      }}
      scroll={{ x: 768 }}
    />
  );
};

export default PersonalReports;
